import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  clientService,
  type CreateClientData,
  type GetClientsParams,
  type UpdateClientData,
} from '@/services/clientService'
import { useApiReady } from '@/hooks/useApiReady'

export const clientKeys = {
  all: ['clients'] as const,
  list: (params?: GetClientsParams) => [...clientKeys.all, 'list', params] as const,
  detail: (clientId: string) => [...clientKeys.all, 'detail', clientId] as const,
  missions: (clientId: string) => [...clientKeys.all, 'missions', clientId] as const,
  shareLink: (clientId: string) => [...clientKeys.all, 'shareLink', clientId] as const,
}

export const clientContactKeys = {
  all: ['clientContacts'] as const,
  options: (search?: string) => [...clientContactKeys.all, 'options', search] as const,
}

export function useClients(params?: GetClientsParams) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => clientService.getClients(params),
    enabled: apiReady,
  })
}

export function useClientContactOptions(search?: string) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: clientContactKeys.options(search),
    queryFn: () => clientService.getClientContactOptions(search),
    enabled: apiReady,
  })
}

export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation<Awaited<ReturnType<typeof clientService.createClient>>, Error, CreateClientData>({
    mutationFn: (data: CreateClientData) => clientService.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientContactKeys.all })
    },
  })
}

type UpdateClientMutationInput = { clientId: string; data: UpdateClientData }

export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation<Awaited<ReturnType<typeof clientService.updateClient>>, Error, UpdateClientMutationInput>({
    mutationFn: ({ clientId, data }: UpdateClientMutationInput) => clientService.updateClient(clientId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientContactKeys.all })
      if (variables?.clientId) {
        queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.clientId) })
      }
    },
  })
}

export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (clientId: string) => clientService.deleteClient(clientId),
    onSuccess: (_data, clientId) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientContactKeys.all })
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) })
    },
  })
}

export function useCreateClientContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clientId, data }: { clientId: string; data: { first_name?: string; last_name?: string; email: string; phone?: string; job_title?: string } }) =>
      clientService.createClientContact(clientId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientContactKeys.all })
      if (variables?.clientId) {
        queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.clientId) })
      }
    },
  })
}

export function useUpdateClientContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clientId, contactId, data }: { clientId: string; contactId: string; data: { first_name?: string; last_name?: string; email?: string; phone?: string; job_title?: string } }) =>
      clientService.updateClientContact(clientId, contactId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientContactKeys.all })
      if (variables?.clientId) {
        queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.clientId) })
      }
    },
  })
}

export function useClientDetail(clientId: string | undefined) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: clientKeys.detail(clientId || ''),
    queryFn: () => clientService.getClient(clientId!),
    enabled: apiReady && !!clientId,
  })
}

export function useClientMissions(clientId: string | null) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: clientKeys.missions(clientId || ''),
    queryFn: () => clientService.getClientMissions(clientId!),
    enabled: apiReady && !!clientId,
  })
}

export function useDeleteClientContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clientId, contactId }: { clientId: string; contactId: string }) =>
      clientService.deleteClientContact(clientId, contactId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientContactKeys.all })
      if (variables?.clientId) {
        queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.clientId) })
      }
    },
  })
}

export function useClientShareLink(clientId: string | null | undefined) {
  const apiReady = useApiReady()

  return useQuery({
    queryKey: clientKeys.shareLink(clientId || ''),
    queryFn: () => clientService.getClientShareLink(clientId!),
    enabled: apiReady && !!clientId,
  })
}

export function useCreateClientShareLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (clientId: string) => clientService.createClientShareLink(clientId),
    onSuccess: (_data, clientId) => {
      if (clientId) {
        queryClient.invalidateQueries({ queryKey: clientKeys.shareLink(clientId) })
      }
    },
  })
}

export function useImportClientsCsv() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (
      clients: Array<{
        name: string
        siren_number?: string
        address?: string
        country_code?: string
        contact_1_first_name?: string
        contact_1_last_name?: string
        contact_1_email?: string
        contact_1_phone?: string
        contact_1_job_title?: string
        contact_2_first_name?: string
        contact_2_last_name?: string
        contact_2_email?: string
        contact_2_phone?: string
        contact_2_job_title?: string
        contact_3_first_name?: string
        contact_3_last_name?: string
        contact_3_email?: string
        contact_3_phone?: string
        contact_3_job_title?: string
      }>
    ) => clientService.importClientsCsv(clients),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: clientContactKeys.all })
    },
  })
}
